import React, { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { ConfirmDialog } from "primereact/confirmdialog";
import { InputSwitch } from "primereact/inputswitch";
import { Toast } from "primereact/toast";
import "../../Styles/components/Share.scss";
import Loading from "../../Components/Loading";
import ShareQrPanel from "../../Components/ShareQrPanel";
import RoleAccess from "../../Components/RoleAccess";
import PlanGroupActions from "../../Redux/transition/plan-group/plan-group.reducer";

const ShareUrl = (props) => {
  const { t } = useTranslation("common");
  const dispatch = useDispatch();
  const toast = useRef(null);

  const {
    visible,
    setShareDialog,
    sharedTier: { plan, shortUrl, planDescription, planId, planGroupId, isLeaderboard: sharedIsLeaderboard } = {},
    headerFromShareProduct,
    generating,
    defMembership,
    onLeaderboardRemoved,
  } = props;

  const removingLeaderBoard = useSelector((state) => state.planGroups.removingLeaderBoard);
  const removedLeaderBoard = useSelector((state) => state.planGroups.removedLeaderBoard);
  const errorRemovingLeaderBoard = useSelector((state) => state.planGroups.errorRemovingLeaderBoard);

  const removeLeaderBoard = useCallback(
    (id) => { dispatch(PlanGroupActions.removeLeaderBoardRequest(id)); },
    [dispatch]
  );

  const leaderBoard = Boolean(sharedIsLeaderboard || props.leaderBoard);
  const hasData = plan && shortUrl;
  const showQr = !leaderBoard && (planId || planGroupId);
  const qrEntityType = planId ? "plan" : "planGroup";
  const qrEntityId = planId || planGroupId;
  const canTurnOff = leaderBoard && Boolean(planGroupId);

  const businessPurpose = defMembership?.business.purpose;
  const leadText = businessPurpose === "SERVICES" ? t("share.pay") : t("share.donate");

  const [state, setState] = useState({
    copiedFull: false,
    copiedUrl: false,
  });
  const [confirmTurnOff, setConfirmTurnOff] = useState(false);
  const [turningOff, setTurningOff] = useState(false);
  const onRemovedRef = useRef(onLeaderboardRemoved);

  useEffect(() => {
    onRemovedRef.current = onLeaderboardRemoved;
  }, [onLeaderboardRemoved]);

  useEffect(() => {
    if (!visible) {
      setState({ copiedFull: false, copiedUrl: false });
      setConfirmTurnOff(false);
    }
  }, [visible]);

  useEffect(() => {
    if (!turningOff) {
      return;
    }

    if (!removingLeaderBoard && removedLeaderBoard) {
      toast.current?.show({
        severity: "success",
        summary: t("product.success_summary"),
        detail: t("product.leaderboard_turnoff_success"),
        life: 3000,
      });
      setTurningOff(false);
      setConfirmTurnOff(false);
      setShareDialog(false);
      if (onRemovedRef.current) {
        onRemovedRef.current();
      }
    }

    if (!removingLeaderBoard && errorRemovingLeaderBoard) {
      toast.current?.show({
        severity: "error",
        summary: t("product.error_summary"),
        detail: t("product.leaderboard_turnoff_error"),
        life: 3000,
      });
      setTurningOff(false);
    }
  }, [
    turningOff,
    removingLeaderBoard,
    removedLeaderBoard,
    errorRemovingLeaderBoard,
    setShareDialog,
    t,
  ]);

  const handleTurnOff = () => {
    if (!planGroupId) {
      return;
    }
    setConfirmTurnOff(false);
    setTurningOff(true);
    removeLeaderBoard(planGroupId);
  };

  return (
    <>
      <Toast ref={toast} baseZIndex={1500} />
      <Dialog
        header={
          leaderBoard
            ? t("share.leaderboard_title")
            : headerFromShareProduct
              ? headerFromShareProduct
              : t("share.plan_title")
        }
        visible={visible}
        style={{ width: showQr ? "46rem" : "40vw", maxWidth: "95vw" }}
        onHide={() => setShareDialog(false)}
        className={`tier_dialog share-plan-dialog${showQr ? " planShare share-url-with-qr" : ""}`}
        id="tier_dialog"
      >
        {(generating || removingLeaderBoard || !hasData) ? (
          <Loading />
        ) : (
          <div style={{ padding: "1rem" }}>
            <p className="share-title">
              {leaderBoard
                ? t("share.leaderboard_hint")
                : t("share.plan_hint_box")}
            </p>

            {showQr ? (
              <div className="share-content-with-qr copy-share">
                <div className="share-link-column">
                  <div className="share-left-boxes">
                    <div className="share-url-top-box">
                      <div className="share-url-copy-row">
                        <p className="short-url-text">{shortUrl}</p>
                        <Button
                          icon={state.copiedUrl ? "pi pi-check" : "pi pi-copy"}
                          className="share-copy-url-btn share-corner-icon-btn p-button-text p-button-plain"
                          tooltip={state.copiedUrl ? t("share.copied") : t("share.copy_plan_link")}
                          tooltipOptions={{ position: "top" }}
                          aria-label={state.copiedUrl ? t("share.copied") : t("share.copy_plan_link")}
                          onClick={() => {
                            navigator.clipboard.writeText(shortUrl);
                            setState((prev) => ({ ...prev, copiedUrl: true }));
                          }}
                        />
                      </div>
                    </div>
                    <div className="productShare-box">
                      <div className="text-dis">
                        <div className="share-plan-title-row">
                          {!leaderBoard && <span className="lead-text">{leadText}</span>}
                          <span className="name-plan">{plan}</span>
                        </div>
                        {planDescription ? (
                          <div className="description-text">{planDescription}</div>
                        ) : null}
                        <p className="short-url-text">{shortUrl}</p>
                      </div>
                    </div>
                  </div>
                  <Button
                    label={state.copiedFull ? t("share.copied") : t("share.copy")}
                    icon="pi pi-copy"
                    className="copy_btn share-copy-full-btn"
                    onClick={() => {
                      const textToCopy = `${leadText} ${plan}${planDescription ? `\n${planDescription}` : ""}\n${shortUrl}`;
                      navigator.clipboard.writeText(textToCopy);
                      setState((prev) => ({ ...prev, copiedFull: true }));
                    }}
                  />
                </div>
                <ShareQrPanel
                  visible={visible}
                  entityType={qrEntityType}
                  entityId={qrEntityId}
                  fileName={`${plan || "share"}-qr.png`}
                />
              </div>
            ) : (
              <div className="share-link-column">
                <div className="planShare-box" style={{ padding: "4px" }}>
                  <div>
                    {!leaderBoard && <span className="lead-text">{leadText}</span>}
                    <span className="name-plan">{plan}</span>
                    <div className="description-text">{planDescription}</div>
                    <div className="share-url-copy-row">
                      <p className="short-url-text">{shortUrl}</p>
                      <Button
                        icon={state.copiedUrl ? "pi pi-check" : "pi pi-copy"}
                        className="share-copy-url-btn share-corner-icon-btn p-button-text p-button-plain"
                        tooltip={state.copiedUrl ? t("share.copied") : t("share.copy")}
                        tooltipOptions={{ position: "top" }}
                        aria-label={state.copiedUrl ? t("share.copied") : t("share.copy")}
                        onClick={() => {
                          navigator.clipboard.writeText(shortUrl);
                          setState((prev) => ({ ...prev, copiedUrl: true }));
                        }}
                      />
                    </div>
                  </div>
                </div>
                <Button
                  label={state.copiedFull ? t("share.copied") : t("share.copy")}
                  icon="pi pi-copy"
                  className="copy_btn share-copy-full-btn"
                  onClick={() => {
                    const textToCopy = `${plan}${planDescription ? `\n${planDescription}` : ""}\n${shortUrl}`;
                    navigator.clipboard.writeText(textToCopy);
                    setState((prev) => ({ ...prev, copiedFull: true }));
                  }}
                />
              </div>
            )}

            {canTurnOff && (
              <RoleAccess roles={["OWNER", "ADMIN"]}>
                <p className="share-settings-title">{t("share.share_settings")}</p>
                <div className="leaderboard-toggle-row">
                  <InputSwitch
                    inputId="leaderboardEnabled"
                    checked={true}
                    onChange={(e) => {
                      if (!e.value) {
                        setConfirmTurnOff(true);
                      }
                    }}
                  />
                  <label htmlFor="leaderboardEnabled">{t("share.leaderboard_enabled")}</label>
                </div>
                <span className="main-hint">{t("share.leaderboard_toggle_hint")}</span>
              </RoleAccess>
            )}
          </div>
        )}
      </Dialog>
      <ConfirmDialog
        visible={confirmTurnOff}
        onHide={() => setConfirmTurnOff(false)}
        className="confirm_close_btn"
        id="leaderboard_dialog"
        style={{ width: "550px" }}
        header={t("product.leaderboard_turnoff_header")}
        message={
          <div style={{ marginTop: "0.6rem" }}>
            <span className="confirm_invite_detail detail">{t("product.leaderboard_turnoff_message")}</span>
          </div>
        }
        accept={handleTurnOff}
        reject={() => setConfirmTurnOff(false)}
        acceptLabel={t("product.leaderboard_turnoff_accept")}
        rejectLabel={t("product.leaderboard_turnoff_reject")}
      />
    </>
  );
};

export default ShareUrl;
